/**
 * V4.45 — Section "Moteur d'expansion réseau" sur la page Entreprises.
 *
 * Affiche 3 blocs orientés business (déterministes, aucun ML) :
 *   1. Introductions à relancer (>30j SANS nouveau contact créé)
 *   2. Entreprises sous-exploitées (1 contact + preuve business)
 *   3. Entreprises à fort potentiel (1 contact + closing élevé + ≥2 signés)
 *
 * Doctrine produit : on ne relance pas mécaniquement après X jours, on relance
 * quand l'introduction n'a généré aucun résultat business.
 */
import { useEffect, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { Lightning, Buildings, Sparkle, TrendUp, ArrowsClockwise } from "@phosphor-icons/react";

const SECTION_STYLES = {
  intros: { icon: ArrowsClockwise, bg: "var(--cf-warning-soft)", border: "var(--cf-warning-border)", color: "var(--cf-warning-text)", label: "Introductions à relancer" },
  sous_exploitees: { icon: Buildings, bg: "var(--cf-primary-soft)", border: "var(--cf-primary-border)", color: "var(--cf-primary-hover)", label: "Entreprises sous-exploitées" },
  fort_potentiel: { icon: Sparkle, bg: "var(--cf-success-soft)", border: "var(--cf-success-border)", color: "var(--cf-success-text)", label: "Entreprises à fort potentiel" },
};

function ActionCard({ kind, title, subtitle, action, metrics, testid }) {
  const s = SECTION_STYLES[kind];
  const Icon = s.icon;
  return (
    <div
      className="bg-white border rounded-2xl p-3 hover:shadow-md transition-shadow"
      style={{ borderColor: s.border }}
      data-testid={testid}
    >
      <div className="flex items-start gap-2 mb-2">
        <div
          className="p-1.5 rounded-lg flex-shrink-0"
          style={{ background: s.bg, color: s.color }}
        >
          <Icon size={14} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-extrabold text-slate-900 text-[13.5px] leading-tight">{title}</div>
          {subtitle && (
            <div className="text-[11px] text-slate-500 font-semibold mt-0.5 leading-snug">{subtitle}</div>
          )}
        </div>
      </div>
      {metrics && (
        <div className="flex items-center gap-3 text-[11px] text-slate-500 font-semibold flex-wrap mb-2">
          {metrics}
        </div>
      )}
      <div
        className="text-[11.5px] font-extrabold rounded-lg px-2 py-1.5 leading-snug"
        style={{ background: s.bg, color: s.color }}
      >
        → {action}
      </div>
    </div>
  );
}

export default function NetworkExpansionSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/network/expansion");
        if (!cancelled) setData(data);
      } catch (err) {
        console.error("Network expansion error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) {
    return (
      <section className="mb-6" data-testid="network-expansion-loading">
        <div className="text-[12px] text-slate-400 font-semibold">Chargement de l&apos;expansion réseau…</div>
      </section>
    );
  }

  const intros = data.intros_a_relancer || [];
  const sous = data.entreprises_sous_exploitees || [];
  const fort = data.entreprises_fort_potentiel || [];
  const totalActions = intros.length + sous.length + fort.length;

  // Doctrine "Safe = Silence" : si aucune action, on n'affiche pas la section
  if (totalActions === 0) return null;

  return (
    <section className="mb-6" data-testid="network-expansion-section">
      <div className="flex items-baseline gap-2 mb-1">
        <Lightning size={16} weight="fill" color="var(--cf-warning)" />
        <h2 className="text-[15px] font-extrabold text-slate-900">Moteur d&apos;expansion réseau</h2>
        <span className="text-[11px] text-slate-500 font-semibold tabular-nums">
          {totalActions} action{totalActions > 1 ? "s" : ""}
        </span>
      </div>
      <div className="text-[11.5px] text-slate-500 font-semibold mb-3 leading-snug max-w-3xl">
        KPI le plus rentable : obtenir des mises en relation qualifiées. On ne relance pas mécaniquement sur le délai — on relance quand l&apos;introduction n&apos;a généré aucun nouveau contact.
      </div>

      {/* Bloc 1 — Intros à relancer */}
      {intros.length > 0 && (
        <div className="mb-3" data-testid="network-intros-relancer">
          <div className="text-[11px] font-extrabold text-amber-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <ArrowsClockwise size={11} weight="bold" />
            Introductions à relancer ({intros.length})
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {intros.slice(0, 6).map((a) => (
              <ActionCard
                key={`intro-${a.name_key}`}
                kind="intros"
                title={a.name}
                subtitle={a.company_name ? `${a.company_name} · demandé il y a ${a.days_since_request}j` : `Demandé il y a ${a.days_since_request}j`}
                metrics={
                  <>
                    <span>{a.dossiers_total} dossier{a.dossiers_total > 1 ? "s" : ""}</span>
                    {a.ca_net_total > 0 && <><span>·</span><span className="text-emerald-700 font-extrabold">{formatEUR(a.ca_net_total)}</span></>}
                  </>
                }
                action="Relance pour obtenir une mise en relation concrète"
                testid={`network-intro-${a.name_key}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Bloc 2 — Entreprises sous-exploitées */}
      {sous.length > 0 && (
        <div className="mb-3" data-testid="network-sous-exploitees">
          <div className="text-[11px] font-extrabold text-blue-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Buildings size={11} weight="bold" />
            Entreprises sous-exploitées ({sous.length})
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {sous.slice(0, 6).map((c) => (
              <ActionCard
                key={`sous-${c.company_key}`}
                kind="sous_exploitees"
                title={c.company_name}
                subtitle={`1 contact (${c.lead_contact_name || "—"})`}
                metrics={
                  <>
                    <span>{c.dossiers_total} dossiers · {c.signed} signé{c.signed > 1 ? "s" : ""}</span>
                    <span>·</span>
                    <span className="text-emerald-700 font-extrabold">{formatEUR(c.ca_net_total)}</span>
                  </>
                }
                action={`Obtenir 2 nouvelles introductions dans l'agence`}
                testid={`network-sous-${c.company_key}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Bloc 3 — Fort potentiel */}
      {fort.length > 0 && (
        <div data-testid="network-fort-potentiel">
          <div className="text-[11px] font-extrabold text-emerald-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <TrendUp size={11} weight="bold" />
            Entreprises à fort potentiel ({fort.length})
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {fort.slice(0, 6).map((c) => (
              <ActionCard
                key={`fort-${c.company_key}`}
                kind="fort_potentiel"
                title={c.company_name}
                subtitle={`1 contact (${c.lead_contact_name || "—"})`}
                metrics={
                  <>
                    <span>{c.signed} signé{c.signed > 1 ? "s" : ""}</span>
                    <span>·</span>
                    <span className="font-extrabold">{c.closing_rate}% transfo</span>
                    <span>·</span>
                    <span className="text-emerald-700 font-extrabold">{formatEUR(c.ca_net_total)}</span>
                  </>
                }
                action="Priorité stratégique : développer cette agence"
                testid={`network-fort-${c.company_key}`}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
